import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-[#3AAFB9] p-1.5">
              <img src="/logo.png" alt="CmdOut" className="w-full h-full object-contain" />
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight">CmdOut</span>
              <span className="text-[#3AAFB9] font-semibold">.io</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: March 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using CmdOut.io ("the Platform"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              CmdOut.io is a marketplace platform that connects content creators with brands for collaboration opportunities. 
              We provide tools for discovering partnerships, managing proposals, communicating, and processing payments.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">To use certain features of the Platform, you must:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Create an account with accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Be at least 18 years old or have parental consent</li>
              <li>Not share your account with others</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Creator Responsibilities</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">As a Creator on CmdOut.io, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide accurate information about your follower counts and engagement</li>
              <li>Deliver content as agreed upon in accepted proposals</li>
              <li>Comply with FTC guidelines for sponsored content disclosure</li>
              <li>Not engage in fraudulent activity or misrepresentation</li>
              <li>Respect intellectual property rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Brand Responsibilities</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">As a Brand on CmdOut.io, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide accurate information about your company and campaigns</li>
              <li>Pay creators as agreed upon in contracts</li>
              <li>Provide clear campaign requirements and expectations</li>
              <li>Respect creators' creative freedom within agreed guidelines</li>
              <li>Not request content that violates laws or platform policies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Payments and Fees</h2>
            <p className="text-muted-foreground leading-relaxed">
              All payments are processed through our secure payment partner, Stripe. CmdOut.io may charge service fees 
              for transactions. Payment terms are established between creators and brands through our contract system. 
              We are not responsible for disputes regarding payment amounts agreed upon between parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Prohibited Conduct</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">Users may not:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Use the Platform for illegal purposes</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>Attempt to circumvent our payment system</li>
              <li>Use automated systems to access the Platform without permission</li>
              <li>Infringe on intellectual property rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Platform and its content are owned by CmdOut.io. Users retain ownership of their own content but grant 
              us a license to display it on the Platform. Creators retain ownership of content they create for brands 
              unless otherwise specified in individual contracts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              CmdOut.io provides the Platform "as is" without warranties. We are not liable for any indirect, incidental, 
              or consequential damages. Our total liability is limited to the fees you have paid us in the past 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may suspend or terminate your account at any time for violation of these terms. You may close your 
              account at any time through your account settings. Termination does not affect obligations incurred prior 
              to termination.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may modify these Terms at any time. We will notify users of material changes via email or Platform 
              notification. Continued use of the Platform after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about these Terms of Service, please contact us at:{' '}
              <a href="mailto:support@cmdout.io" className="text-[#3AAFB9] hover:underline">
                support@cmdout.io
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} CmdOut.io. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <button onClick={() => navigate('/privacy')} className="hover:text-foreground transition-colors">Privacy</button>
            <button onClick={() => navigate('/terms')} className="text-foreground font-medium">Terms</button>
            <button onClick={() => navigate('/contact')} className="hover:text-foreground transition-colors">Contact</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
